import React, { useState } from 'react';
import { Box, Button, FlatList, HStack, Input, Pressable, VStack } from 'native-base';
import {
    LocationDetail,
    Retailer,
    MenuType,
    FulfillmentOptionsCardProp,
    OrderType,
    i18nFn,
} from '@springbig/shared';
import { useShopStyles } from '~/shared/styles';
import { Caption, H1 } from '~/shared/Typography';
import { SpIcon } from '~/shared/Icon';
import { faArrowLeft, faSearch } from '@fortawesome/free-solid-svg-icons';
import { storeSchedule } from './StoreModal';

export type StoreLocationsType = 'page' | 'modal';

type StoreLocationsProps = {
    stores: Array<{
        locationDetail: LocationDetail;
        retailer: Retailer;
    }>;
    setRetailer: (retailer: Retailer) => void;
    setMenuType: (menuType: MenuType) => void;
    setFullfillmentOption: (value: FulfillmentOptionsCardProp) => void;
    type: StoreLocationsType;
    t: i18nFn;
    internalRetailer?: Retailer;
    isMerchantGeoLocation?: boolean;
};

export const StoreLocations = ({
    stores,
    setRetailer,
    setMenuType,
    setFullfillmentOption,
    type = 'page',
    internalRetailer,
    t,
    isMerchantGeoLocation
}: StoreLocationsProps) => {

    const [filteredStore, setFilteredStores] = useState(stores);
    const [selectType, setSelectType] = useState(false);
    const [selectedRetailer, setSelectedRetailer] = useState<Retailer>();

    const {
        icon,
        sub_text,
        button_background,
        pressable_text,
        section_background,
        primary_color,
    } = useShopStyles();

    const setFullfillmentOptionMethod = (selectedRetailer: Retailer) => {

        const availableFulfillmentOptions: Array<FulfillmentOptionsCardProp> = [];
        if (selectedRetailer?.fulfillmentOptions.pickup) {
            availableFulfillmentOptions.push({ key: OrderType.Pickup, label: 'pickup', isOpen: storeSchedule(OrderType.Pickup, selectedRetailer) });
        }

        if (selectedRetailer?.fulfillmentOptions.curbsidePickup) {
            availableFulfillmentOptions.push({ key: OrderType.CurbsidePickup, label: 'curbside pickup', isOpen: storeSchedule(OrderType.CurbsidePickup, selectedRetailer) });
        }

        if (selectedRetailer?.fulfillmentOptions.driveThruPickup) {
            availableFulfillmentOptions.push({ key: OrderType.DriveThruPickup, label: 'drive thru pickup', isOpen: storeSchedule(OrderType.DriveThruPickup, selectedRetailer) });
        }

        if (selectedRetailer?.fulfillmentOptions.delivery) {
            availableFulfillmentOptions.push({ key: OrderType.Delivery, label: 'delivery', isOpen: storeSchedule(OrderType.Delivery, selectedRetailer) });
        }

        if (availableFulfillmentOptions && availableFulfillmentOptions[0]) {
            setFullfillmentOption(availableFulfillmentOptions[0]);
        }
    };

    if (selectType) {
        return <>
            <Pressable p={4} pb={8} onPress={() => setSelectType(false)}>
                <HStack alignItems='center'>
                    <SpIcon icon={faArrowLeft} color={icon} />
                    <Caption text='general.back' paddingX={2} color={icon} />
                </HStack>
            </Pressable>
            <FlatList
                w={'100%'}
                data={['Medical', 'Recreational']}
                renderItem={({ item, index }) =>

                    <Button
                        key={index}
                        bg={section_background}
                        borderColor={button_background}
                        rounded={'md'}
                        borderWidth={1}
                        my={2}
                        py={2}
                        width={'80%'}
                        mx={'auto'}
                        alignItems={'center'}
                        onPress={() => {

                            if (selectedRetailer) {
                                setRetailer(selectedRetailer);
                                setMenuType(item.toString().toUpperCase() as MenuType);
                                setFullfillmentOptionMethod(selectedRetailer);
                            }
                        }}>
                        <Caption color={button_background}>
                            { item }
                        </Caption>
                    </Button>
                }
            />
        </>;
    }

    return (
        <VStack
            alignItems={'center'}
            space={2}
            bg={type === 'page' ? primary_color : section_background}
            p={2}
            rounded={'sm'}
        >
            {type === 'page' ?
                <H1 color={button_background}>
                    { t('shop.pick_your_location') }
                </H1>
                : <></>
            }
            <Input
                mx={4}
                px={2}
                h={10}
                w={'100%'}
                borderWidth={0}
                bg={type === 'page' ? section_background : primary_color}
                placeholder="search stores"
                rightElement={
                    <Box px={2}>
                        <SpIcon size={20} icon={faSearch} color={icon} />
                    </Box>
                }
                onChangeText={(text: string) => {

                    if (text) {
                        const searchFilteredStores = filteredStore.filter(
                            (e) =>

                                e.retailer.name.toLocaleLowerCase().includes(text.toLocaleLowerCase())
                        );
                        setFilteredStores(searchFilteredStores);
                    }
                    else {
                        setFilteredStores(stores);
                    }
                }}
                color={sub_text}
            />
            <FlatList
                data={filteredStore}
                w={'100%'}
                renderItem={({ item, index }) =>

                    <Pressable
                        key={index}
                        bg={item.retailer.id === internalRetailer?.id ? button_background : section_background}
                        borderColor={item.retailer === internalRetailer?.id ? section_background : button_background}
                        rounded={'md'}
                        borderWidth={1}
                        my={2}
                        p={2}
                        onPress={() => {

                            if (item.retailer.menuTypes.length >= 2) {
                                setSelectType(true);
                                setSelectedRetailer(item.retailer);
                            }
                            else {
                                setMenuType(item.retailer.menuTypes[0]);
                                setRetailer(item.retailer);
                                setFullfillmentOptionMethod(item.retailer);
                            }
                        }}
                    >
                        <HStack justifyContent={'space-between'} alignItems={'center'} space={2}>
                            <Caption color={item.retailer.id === internalRetailer?.id ? section_background : pressable_text} numberOfLines={2}>
                                { item.retailer.name }
                            </Caption>
                            {!isMerchantGeoLocation && <Caption color={item.retailer.id === internalRetailer?.id ? section_background : pressable_text} textAlign={'right'} minW={'72px'}>
                                { item?.locationDetail?.distance?.toFixed(1) } { item?.locationDetail?.unit === 'MI' ? 'miles' : 'km' }
                            </Caption>}
                        </HStack>
                    </Pressable>
                }
            />
            { filteredStore.length === 0 &&
                <Caption mb={4}>
                    { t('shop.no_matching_location') }
                </Caption>
            }
        </VStack>
    );
};
